import React, { useState, useLayoutEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import DepartmentService from '@/services/Department.service';
import { useSnackbar } from 'notistack';
import { Department, ParentDepartment } from '@/types/Department.type';
import { Messages } from '@/common/constants';
import { Box, Grid, IconButton } from '@mui/material';
import PageTitle from '@/components/common/PageTitle';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import Loading from '@/components/common/Loading';
import URL from '@/common/routes/Url';
import useSidebar from '@/common/hooks/useSidebar';

interface DelType {
  id: number;
  name: string;
}

const DepartmentList: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [departments, setDepartments] = useState<Array<Department & ParentDepartment>>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [delDepartment, setDelDepartment] = useState<DelType>({ id: 0, name: '' });

  const getDepartments = () => {
    DepartmentService.getAll({ params: { sortKey: 'id' } })
      .then((response) => {
        setDepartments(response.data);
        setLoading(false);
      })
      .catch((e) => {
        enqueueSnackbar(Messages.select.failure);
      });
  };

  useLayoutEffect(() => {
    getDepartments();
  }, []);

  const createDepartment = () => {
    navigate(`${URL.departments}/add`);
  };

  const editDepartment = (id: number) => {
    navigate(`${URL.departments}/${id}`);
  };

  const { reload } = useSidebar();

  const deleteDepartment = async (id: number) => {
    setLoading(true);
    try {
      await DepartmentService.delete(id);
    } catch (error: any) {
      setLoading(false);
      enqueueSnackbar(error.response.data.message);
      return;
    }

    await DepartmentService.getAll({ params: { sortKey: 'id' } })
      .then((response) => {
        setDepartments(response.data);
      })
      .catch((e) => {
        enqueueSnackbar(Messages.select.failure);
      });

    reload();
    setLoading(false);
    enqueueSnackbar(Messages.delete.success, { variant: 'info' });
  };

  const handleDialog = (id: number, name: string) => {
    setDelDepartment({
      id: id,
      name: name,
    });
    setOpen(true);
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={12} lg={8}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>部署マスタ</PageTitle>
        </Box>
        <Box sx={{ marginBottom: '8px' }}>
          <Button variant="contained" color="primary" onClick={() => createDepartment()}>
            追加画面へ
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left">部署名</TableCell>
                <TableCell align="left">略名</TableCell>
                <TableCell align="left">親部署名</TableCell>
                <TableCell align="left">表示順</TableCell>
                <TableCell align="center" width="150"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="left">{row.name}</TableCell>
                  <TableCell align="left">{row.shortName}</TableCell>
                  <TableCell align="left">{row.parentDepartment ? row.parentDepartment.name : ''}</TableCell>
                  <TableCell align="left">{row.sortNo}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => editDepartment(row.id)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDialog(row.id, row.name)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <ConfirmDialog
          open={open}
          onClose={() => setOpen(false)}
          onOk={() => {
            setOpen(false);
            deleteDepartment(delDepartment.id);
          }}
        >
          {delDepartment.name}を本当に削除しますか？
        </ConfirmDialog>
      </Grid>
    </Grid>
  );
};
export default DepartmentList;
